import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { FiCalendar, FiChevronDown } from "react-icons/fi";
import { TbLayoutSidebarLeftExpandFilled, TbLayoutSidebarRightExpandFilled } from "react-icons/tb";
import { HiWrenchScrewdriver } from "react-icons/hi2";
import { FaChartPie } from "react-icons/fa";
import KnowledgeGraphVizContainer from "../../containers/configuration/mkg/KnowledgeGraphVizContainer";
import MetricListComponent from "../../components/configuration/MetricListComponent";
import BenchmarkListComponent from "../../components/configuration/BenchmarkListComponent";
import AddDescriptionContainer from "../../containers/configuration/snowflake/AddDescriptionContainer";
import ViewMetricByModelContainer from "../../containers/configuration/ViewMetricByModelContainer";
import { ColumnInfo, Header, ModelInfo } from "build-ts/metadata";
import KGColumnListComponent from "../../components/configuration/KGColumnListComponent";
import { ColumnType } from "build-ts/enums";
import { ConfigurationContextProvider } from "context/ConfigurationContext";
import { getAllKGColumns, getDataModelHeaders } from "services/DataModelService";
import showToast from "hooks/useCustomToast";
import { IoGitCompareOutline } from "react-icons/io5";
import { MdBarChart, MdNumbers } from "react-icons/md";
import { PiTarget } from "react-icons/pi";
import ThoughtSpotEmbed from "../../components/configuration/ThoughtSpotEmbed";
import { useUser } from "../../context/UserContext";
import { useAnalystChat } from "context/AnalystChatContext";
import { AiOutlineApartment } from "react-icons/ai";
import { BiData } from "react-icons/bi";
import { useLocation, useNavigate, useMatch } from "react-router-dom";

const ConfigurationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch("/mkg/models/:id");  // useMatch is used to check whether the current URL matches a specific route pattern.
  const tsMatch = useMatch("/mkg/thoughtspot/:tsId");

  const { setIsNavbarCollapsed } = useAnalystChat();

  const [modelList, setModelList] = useState<Header[]>([]);

  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isKGOpen } = useDisclosure({ defaultIsOpen: true });
  const { userId } = useUser();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [activeButton, setActiveButton] = useState<string>("ViewMKGContainer");
  const [selectedLink, setSelectedLink] = useState<string>("ViewMKGContainer");
  const [modelInfo, setModelInfo] = useState<ModelInfo | undefined>(undefined);
  const [addedColumns, setAddedColumns] = useState<ColumnInfo[]>([]);
  const [removedColumns, setRemovedColumns] = useState<ColumnInfo[]>([]);
  const [showMetricsInSidebar, setShowMetricsInSidebar] = useState<boolean>(false);
  const [showBenchmarksInSidebar, setShowBenchmarksInSidebar] = useState<boolean>(false);
  const [showKGColumnsInSidebar, setShowKGColumnsInSidebar] = useState<boolean>(false);
  const [showAttributesInSidebar, setShowAttributesInSidebar] = useState<boolean>(false);
  const [showMeasuresInSidebar, setShowMeasuresInSidebar] = useState<boolean>(false);
  const [showTemporalInSidebar, setShowTemporalInSidebar] = useState<boolean>(false);
  const [benchmarkList, setBenchmarkList] = useState<Header[]>([]);
  const [attributeList, setAttributeList] = useState<Header[]>([]);
  const [measureList, setMeasureList] = useState<Header[]>([]);
  const [temporalList, setTemporalList] = useState<Header[]>([]);
  const [showMKGNetwork, setShowMKGNetwork] = useState<boolean>(true); // Default to true to show on page load
  const [shouldReloadKG, setShouldReloadKG] = useState<boolean>(false); // Boolean to trigger reloads

  // UseEffect hook to fetch model list when the component mounts
  const fetchModelList = async () => {
    try {
      const data = await getDataModelHeaders(false /* Include only target models */);
      if (data) {
        setModelList(data);
      }
    } catch (error) {
      showToast("Error in REACT_APP_LIST_DATMODEL", "", "error");
    }
  };

  useEffect(() => {
    fetchModelList();
  }, []);

  // Effect to handle the URL pathname
  useEffect(() => {
    if (location.pathname === "/mkg") {
      // default view
      setShowMKGNetwork(true);
      setIsNavbarCollapsed(true);
      setActiveButton("ViewMKGContainer");
      setSelectedLink("ViewMKGContainer");
    } else if (location.pathname === "/mkg/models") {
      setShowMKGNetwork(false);
      setIsNavbarCollapsed(true);
      setActiveButton("ankoModel");
      setSelectedLink("ankoModel");
    } else if (location.pathname === "/mkg/models/new") {
      setShowMKGNetwork(false);
      setIsNavbarCollapsed(true);
      setActiveButton("createModel");
      setSelectedLink("createModel");
    }
    else if (match) {
      setShowMKGNetwork(false);
      setIsNavbarCollapsed(true);
      setActiveButton("updateModel");
      setSelectedLink("updateModel");
    } else if (location.pathname === "/mkg/thoughtspot" || tsMatch) {
      setShowMKGNetwork(false);
      setIsNavbarCollapsed(true);
      setActiveButton("thoughtspotEmbed");
      setSelectedLink("thoughtspotEmbed");
    }
  }, [location.pathname, match, tsMatch]);

  const fetchKgColumns = async () => {
    try {
      const kgColumns = await getAllKGColumns();
      if (kgColumns) {
        setAttributeList(kgColumns.filter((column: Header) => column.column_type === ColumnType.ATTRIBUTE));
        setMeasureList(kgColumns.filter((column: Header) => column.column_type === ColumnType.MEASURE));
        setTemporalList(kgColumns.filter((column: Header) => column.column_type === ColumnType.TEMPORAL));
      }
    } catch (error) {
      showToast("Error fetching KG columns", "", "error");
    }
  };

  // Function to reload the Knowledge Graph Viz Container
  const handleReloadKnowledgeGraph = () => {
    // Set to true to trigger reload, then immediately back to false
    setShouldReloadKG(true);
    setTimeout(() => setShouldReloadKG(false), 100);
  };

  const handleBackToSidebar = () => {
    setShowMetricsInSidebar(false);
    setShowBenchmarksInSidebar(false);
    setShowAttributesInSidebar(false);
    setShowMeasuresInSidebar(false);
    setShowTemporalInSidebar(false);
  };

  const renderComponent = () => {
    // If showMKGNetwork is true, always show ViewMKGContainer
    if (showMKGNetwork) {
      return <KnowledgeGraphVizContainer shouldReload={shouldReloadKG} />;
    }

    // Otherwise, show the selected component
    const componentMapping: Record<string, JSX.Element | null> = {
      createModel: (
        <AddDescriptionContainer
          modelInfo={modelInfo}
          updateFlag={false}
          onBack={() => navigate("/mkg/models")}
          onReloadModelList={fetchModelList}
        />
      ),
      updateModel: (
        <AddDescriptionContainer
          updateFlag={true}
          onBack={() => navigate("/mkg/models")}
          onReloadModelList={fetchModelList}
          addedColumns={addedColumns}
          removedColumns={removedColumns}
        />
      ),
      ankoModel: <ViewMetricByModelContainer modelList={modelList} setModelList={setModelList} />,
      thoughtspotEmbed: (
        <ThoughtSpotEmbed
          setModelInfo={setModelInfo}
          modelList={modelList}
          tsModelId={tsMatch?.params?.tsId}
          setAddedColumns={setAddedColumns}
          setRemovedColumns={setRemovedColumns}
        />
      )
    };

    return componentMapping[selectedLink] || null;
  };

  return (
    <ConfigurationContextProvider>
      <Grid
        templateAreas={`
          "sidebar main"
        `}
        gridTemplateRows="1fr"
        gridTemplateColumns={isSidebarCollapsed ? "40px 1fr" : "18% 82%"}
        height="100%"
        p={0}
      >
        <GridItem area="sidebar" bg="gray.50" borderRightWidth="1px" borderColor="gray.300">
          {isSidebarCollapsed ? (
            <VStack pt={4} spacing={0}>
              <IconButton
                aria-label="Expand sidebar"
                icon={<Icon as={TbLayoutSidebarLeftExpandFilled} boxSize={7} />}
                variant="ghost"
                size="lg"
                onClick={() => setIsSidebarCollapsed(false)}
                _hover={{ bg: "purple.50" }}
              />
            </VStack>
          ) : showMetricsInSidebar ? (
            <MetricListComponent
              onBack={handleBackToSidebar}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : showBenchmarksInSidebar ? (
            <BenchmarkListComponent
              onBack={handleBackToSidebar}
              benchmarkList={benchmarkList}
              setBenchmarkList={setBenchmarkList}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : showMeasuresInSidebar ? (
            <KGColumnListComponent
              onBack={handleBackToSidebar}
              kgColumnList={measureList}
              setKGColumnList={setMeasureList}
              columnType={ColumnType.MEASURE}
              refreshKgColumns={fetchKgColumns}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : showAttributesInSidebar ? (
            <KGColumnListComponent
              onBack={handleBackToSidebar}
              kgColumnList={attributeList}
              setKGColumnList={setAttributeList}
              columnType={ColumnType.ATTRIBUTE}
              refreshKgColumns={fetchKgColumns}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : showTemporalInSidebar ? (
            <KGColumnListComponent
              onBack={handleBackToSidebar}
              kgColumnList={temporalList}
              setKGColumnList={setTemporalList}
              columnType={ColumnType.TEMPORAL}
              refreshKgColumns={fetchKgColumns}
              onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
            />
          ) : (
            <VStack spacing="3" alignItems="stretch" px={1} py={1} mt={3}>
              <Box>
                <HStack
                  as="button"
                  type="button"
                  width="full"
                  spacing="3"
                  justifyContent="space-between"
                  py={2}
                  px={2}
                  borderRadius="md"
                  _hover={{ bg: "purple.50", color: "black" }}
                  onClick={() => {
                    setShowMKGNetwork(true);
                    navigate("/mkg");
                    setActiveButton("ViewMKGContainer");
                    setSelectedLink("ViewMKGContainer");
                  }}
                  textAlign="left"
                >
                  <HStack spacing="3" flex={1} minW={0}>
                    <Icon
                      as={IoGitCompareOutline}
                      boxSize={6}
                      color="fg.default"
                      flexShrink={0}
                    />
                    <Text as="span" color="fg.default" fontWeight={600} fontSize="md">
                      Metric Knowledge Graph
                    </Text>
                  </HStack>
                  <IconButton
                    aria-label="Collapse sidebar"
                    icon={<Icon as={TbLayoutSidebarRightExpandFilled} boxSize={7} />}
                    variant="ghost"
                    size="sm"
                    flexShrink={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSidebarCollapsed(true);
                    }}
                    _hover={{ bg: "purple.50" }}
                  />
                </HStack>
                <Collapse in={isKGOpen} animateOpacity>
                  <VStack spacing="3" alignItems="stretch" px={8} py={3}>
                    <VStack spacing="2" alignItems="start">
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={MdBarChart} boxSize={6} />}
                        _hover={{ bg: "purple.600" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Metrics
                          navigate("/mkg");
                          setShowMetricsInSidebar(true);
                          setShowBenchmarksInSidebar(false);
                          setShowKGColumnsInSidebar(false);
                        }}
                        bg={
                          showMetricsInSidebar
                            ? "purple.700"
                            : "purple.700"
                        }
                        color={
                          showMetricsInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Metrics
                        </Text>
                      </Button>
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={PiTarget} boxSize={6} />}
                        _hover={{ bg: "orange.400" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Benchmarks
                          navigate("/mkg");
                          setShowBenchmarksInSidebar(true);
                          setShowMetricsInSidebar(false);
                          setShowKGColumnsInSidebar(false);
                        }}
                        bg={
                          showBenchmarksInSidebar
                            ? "orange.500"
                            : "orange.500"
                        }
                        color={
                          showBenchmarksInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Benchmarks
                        </Text>
                      </Button>
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={MdNumbers} boxSize={6} />}
                        _hover={{ bg: "green.500" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Measures
                          navigate("/mkg");
                          setShowMeasuresInSidebar(true);
                          setShowMetricsInSidebar(false);
                          setShowBenchmarksInSidebar(false);
                        }}
                        bg={
                          showKGColumnsInSidebar
                            ? "green.600"
                            : "green.600"
                        }
                        color={
                          showKGColumnsInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Measures
                        </Text>
                      </Button>
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={FaChartPie} boxSize={6} />}
                        _hover={{ bg: "blue.500" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Attributes
                          navigate("/mkg");
                          setShowAttributesInSidebar(true);
                          setShowMetricsInSidebar(false);
                          setShowBenchmarksInSidebar(false);
                        }}
                        bg={
                          showKGColumnsInSidebar
                            ? "blue.600"
                            : "blue.600"
                        }
                        color={
                          showKGColumnsInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Attributes
                        </Text>
                      </Button>
                      <Button
                        variant="tertiary"
                        justifyContent="start"
                        iconSpacing="3"
                        leftIcon={<Icon as={FiCalendar} boxSize={6} />}
                        _hover={{ bg: "yellow.500" }}
                        onClick={() => {
                          setShowMKGNetwork(true); // Show MKG when clicking Temporal
                          navigate("/mkg");
                          setShowTemporalInSidebar(true);
                          setShowMetricsInSidebar(false);
                          setShowBenchmarksInSidebar(false);
                        }}
                        bg={
                          showKGColumnsInSidebar
                            ? "yellow.600"
                            : "yellow.600"
                        }
                        color={
                          showKGColumnsInSidebar
                            ? "white"
                            : "white"
                        }
                        size="sm"
                      >
                        <Text mb={0} fontWeight={600}>
                          Datetime
                        </Text>
                      </Button>
                    </VStack>
                  </VStack>
                </Collapse>
              </Box>
              <Box px={2} py={1}>
                <Divider borderColor="gray.300" my={1} />
              </Box>
              <Box>
                <Button
                  variant="tertiary"
                  onClick={onToggle} // Just toggle the Integration section, don't hide MKG
                  justifyContent="space-between"
                  width="full"
                  _hover={{ bg: "purple.100", color: "black" }}
                >
                  <HStack spacing="3">
                    <Icon
                      as={AiOutlineApartment}
                      boxSize={6}
                      color="fg.default"
                    />
                    <Text as="span" color="fg.default" fontWeight={600} fontSize="lg">
                      Data
                    </Text>
                  </HStack>
                  <PopoverIcon isOpen={isOpen} />
                </Button>
                <Collapse in={isOpen} animateOpacity>
                  <Stack spacing="1" alignItems="stretch" ps="8" py="1">
                    <Button
                      variant="tertiary"
                      justifyContent="space-between"
                      width="full"
                      _hover={{ bg: "purple.50", color: "black" }}
                      onClick={() => {
                        setShowMKGNetwork(false); // Hide MKG when clicking Anko Model
                        setActiveButton("ankoModel");
                        setSelectedLink("ankoModel");
                        navigate("/mkg/models");
                      }}
                      bg={
                        activeButton === "ankoModel" ? "purple.700" : undefined
                      }
                      color={
                        activeButton === "ankoModel" ? "white" : "fg.default"
                      }
                    >
                      <HStack>
                        <Icon
                          as={BiData}
                          boxSize={6}
                          color={activeButton === "ankoModel" ? "white" : "fg.default"}
                        />
                        <Text as="span" color={activeButton === "ankoModel" ? "white" : "fg.default"} fontWeight={500} fontSize="md">
                          Data Models
                        </Text>
                      </HStack>
                    </Button>
                    {userId === "02c73738-9481-4d0f-85c4-e927c6a09d09" && (
                      <Button
                        variant="tertiary"
                        justifyContent="space-between"
                        width="full"
                        _hover={{ bg: "purple.50", color: "black" }}
                        onClick={() => {
                          setShowMKGNetwork(false); // Hide MKG when clicking ThoughtSpot
                          navigate("/mkg/thoughtspot");
                          setIsSidebarCollapsed(true);
                          setActiveButton("thoughtspotEmbed");
                          setSelectedLink("thoughtspotEmbed");
                        }}
                        bg={
                          activeButton === "thoughtspotEmbed" ? "purple.700" : undefined
                        }
                        color={
                          activeButton === "thoughtspotEmbed" ? "white" : "fg.default"
                        }
                      >
                        <HStack>
                          <Icon
                            as={HiWrenchScrewdriver}
                            boxSize={5}
                            color={activeButton === "thoughtspotEmbed" ? "white" : "fg.default"}
                          />
                          <Text as="span" color={activeButton === "thoughtspotEmbed" ? "white" : "fg.default"} fontWeight={600} fontSize="md">
                            Data Connections
                          </Text>
                        </HStack>
                      </Button>
                    )}
                  </Stack>
                </Collapse>
              </Box>
            </VStack>
          )}
        </GridItem>
        <GridItem
          area="main"
          overflowY="auto"
          height="100%"
          sx={{
            "::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none", // IE and Edge
            scrollbarWidth: "none", // Firefox
          }}
        >
          <Stack p={0} height="100%">{renderComponent()}</Stack>
        </GridItem>
      </Grid>
    </ConfigurationContextProvider>
  );
};

export default ConfigurationPage;

export const PopoverIcon = (props: { isOpen: boolean }) => {
  const iconStyles = {
    transform: props.isOpen ? "rotate(-180deg)" : undefined,
    transition: "transform 0.2s",
    transformOrigin: "center",
  };
  return <Icon aria-hidden as={FiChevronDown} __css={iconStyles} />;
};
